import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { finalize, Subscription } from 'rxjs';
import {
  DashboardMetricPoint,
  DashboardMetrics,
  DashboardRange,
  DashboardService,
} from '../../../core/services/dashboard.service';

interface TrendGraphPoint extends DashboardMetricPoint {
  x: number;
  y: number;
  hitX: number;
  hitWidth: number;
  showLabel: boolean;
}

interface RequestStatusPoint {
  label: string;
  scopeLabel: string;
  value: number;
  color: string;
}

@Component({
  selector: 'app-metrics-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    MatSelectModule,
  ],
  templateUrl: './metrics-dashboard.component.html',
  styleUrl: './metrics-dashboard.component.scss'
})
export class MetricsDashboardComponent implements OnInit, OnDestroy {

  readonly periods: { value: DashboardRange; label: string }[] = [
    { value: '1d', label: 'Today' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '15d', label: 'Last 15 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '60d', label: 'Last 60 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: 'custom', label: 'Custom Range' },
  ];

  readonly maxDate = new Date();
  readonly customRangeForm = new FormGroup({
    fromDate: new FormControl<Date | null>(this.shiftDate(new Date(), -29), Validators.required),
    toDate: new FormControl<Date | null>(new Date(), Validators.required),
  });

  readonly chartColors = ['#1765b8', '#169988', '#dc9b3b', '#8869bb', '#ca6b73', '#71859a'];
  readonly chartWidth = 760;
  readonly chartHeight = 230;
  readonly chartPaddingLeft = 42;
  readonly chartPaddingRight = 14;
  readonly chartPaddingTop = 16;
  readonly chartPaddingBottom = 34;

  selectedRange: DashboardRange = '30d';
  metrics: DashboardMetrics | null = null;
  activeTrendPoint: TrendGraphPoint | null = null;
  // Keep the two charts' hover tooltips independent when their dates overlap.
  activeRequestTrendPoint: TrendGraphPoint | null = null;
  loading = false;
  errorMessage = '';

  private requestSubscription?: Subscription;

  constructor(private readonly dashboardService: DashboardService) {}

  ngOnInit(): void {
    this.loadDashboard();
  }

  ngOnDestroy(): void {
    this.requestSubscription?.unsubscribe();
  }

  selectRange(range: DashboardRange): void {
    this.selectedRange = range;
    this.errorMessage = '';
    if (range !== 'custom') {
      this.loadDashboard();
    }
  }

  applyCustomRange(): void {
    this.customRangeForm.markAllAsTouched();
    const { fromDate, toDate } = this.customRangeForm.getRawValue();
    if (!fromDate || !toDate) {
      this.errorMessage = 'Select both a start date and an end date.';
      return;
    }
    const fromDateValue = this.formatDate(fromDate);
    const toDateValue = this.formatDate(toDate);
    if (fromDateValue > toDateValue) {
      this.errorMessage = 'The start date must be on or before the end date.';
      return;
    }
    if (toDateValue > this.formatDate(this.maxDate)) {
      this.errorMessage = 'The end date cannot be in the future.';
      return;
    }
    if (this.customRangeForm.invalid) {
      this.errorMessage = 'Enter valid start and end dates.';
      return;
    }
    this.loadDashboard();
  }

  refresh(): void {
    if (this.selectedRange === 'custom') {
      this.applyCustomRange();
      return;
    }
    this.loadDashboard();
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat('en-IN').format(value);
  }

  colorForIndex(index: number): string {
    return this.chartColors[index % this.chartColors.length];
  }

  get summaryCards(): { label: string; value: number; icon: string; tone: string; note: string; badge: string }[] {
    const summary = this.metrics?.summary;
    if (!summary) {
      return [];
    }
    return [
      {
        label: 'Open Backlog',
        value: summary.openComplaints,
        icon: 'inbox',
        tone: 'teal',
        note: 'Open right now',
        badge: 'Live',
      },
      {
        label: 'New Complaints',
        value: summary.newComplaintsInRange,
        icon: 'trending_up',
        tone: 'blue',
        note: this.metrics?.rangeLabel.replace(/ days$/, ' Days') ?? 'Selected Period',
        badge: 'Period',
      },
      {
        label: 'Reopened',
        value: summary.reopenedComplaints,
        icon: 'replay',
        tone: 'violet',
        note: 'Currently open',
        badge: 'Live',
      },
      {
        label: 'Overdue',
        value: summary.overdueComplaints,
        icon: 'error_outline',
        tone: 'rose',
        note: 'Past the due date',
        badge: 'Live',
      },
      {
        label: 'Due Today',
        value: summary.dueToday,
        icon: 'event_available',
        tone: 'amber',
        note: 'Needs attention today',
        badge: 'Live',
      },
      {
        label: 'Due Tomorrow',
        value: summary.dueTomorrow,
        icon: 'event',
        tone: 'slate',
        note: 'Coming up next',
        badge: 'Live',
      },
    ];
  }

  get requestSummaryCards(): { label: string; value: number; icon: string; tone: string; note: string; badge: string }[] {
    const requestMetrics = this.metrics?.userRequestMetrics;
    if (!requestMetrics) {
      return [];
    }

    return [
      {
        label: 'Pending User Requests',
        value: requestMetrics.pendingRequests,
        icon: 'hourglass_top',
        tone: 'amber',
        note: 'Awaiting review',
        badge: 'Pending',
      },
      {
        label: 'Approved Requests',
        value: requestMetrics.approvedInRange,
        icon: 'how_to_reg',
        tone: 'teal',
        note: this.metrics?.rangeLabel ?? 'Selected period',
        badge: 'Selected Period',
      },
      {
        label: 'Rejected Requests',
        value: requestMetrics.rejectedInRange,
        icon: 'person_off',
        tone: 'rose',
        note: this.metrics?.rangeLabel ?? 'Selected period',
        badge: 'Selected Period',
      },
    ];
  }

  get requestTrendTotal(): number {
    // Sum zero-filled buckets so this matches the total displayed above the request chart.
    return (this.metrics?.userRequestMetrics?.requestTrend ?? [])
      .reduce((total, point) => total + point.value, 0);
  }

  get requestStatusPoints(): RequestStatusPoint[] {
    const requestMetrics = this.metrics?.userRequestMetrics;
    if (!requestMetrics) {
      return [];
    }

    // Keep scope visible in the legend: pending is a snapshot, but decisions are period-scoped.
    return [
      { label: 'Pending', scopeLabel: 'Current', value: requestMetrics.pendingRequests, color: '#c28a37' },
      { label: 'Approved', scopeLabel: 'Selected Period', value: requestMetrics.approvedInRange, color: '#168578' },
      { label: 'Rejected', scopeLabel: 'Selected Period', value: requestMetrics.rejectedInRange, color: '#cc6c6d' },
    ];
  }

  get requestStatusSegments(): { color: string; dashArray: string; dashOffset: number }[] {
    return this.createDonutSegments(this.requestStatusPoints);
  }

  get statusTotal(): number {
    return (this.metrics?.complaintStatuses ?? []).reduce((total, point) => total + point.value, 0);
  }

  get departmentMetrics(): { department: DashboardMetricPoint; severities: DashboardMetricPoint[] }[] {
    const severitiesByDepartment = new Map<string, DashboardMetricPoint[]>();
    for (const severity of this.metrics ? this.metrics.departmentSeverityBreakdown : []) {
      const entries = severitiesByDepartment.get(severity.key) ?? [];
      entries.push(severity);
      severitiesByDepartment.set(severity.key, entries);
    }

    return (this.metrics?.departmentWorkload ?? []).map(department => ({
      department,
      severities: severitiesByDepartment.get(department.key) ?? [],
    }));
  }

  severityTone(label: string): 'low' | 'medium' | 'high' | 'neutral' {
    const normalizedLabel = label.trim().toLowerCase();
    if (normalizedLabel.startsWith('low')) {
      return 'low';
    }
    if (normalizedLabel.startsWith('med')) {
      return 'medium';
    }
    if (normalizedLabel.startsWith('high') || normalizedLabel.startsWith('critical')) {
      return 'high';
    }
    return 'neutral';
  }

  trendTooltip(point: DashboardMetricPoint): string {
    return `${this.trendPeriod(point)}: ${this.trendCountLabel(point, 'complaint')}`;
  }

  trendCountLabel(point: DashboardMetricPoint, singularUnit: string): string {
    return `${this.formatNumber(point.value)} ${point.value === 1 ? singularUnit : `${singularUnit}s`}`;
  }

  requestTrendTooltip(point: DashboardMetricPoint): string {
    return `${this.trendPeriod(point)}: ${this.trendCountLabel(point, 'user request')}`;
  }

  trendPeriod(point: DashboardMetricPoint): string {
    // Both trend charts share the same bucket format and date-label rules.
    const granularity = this.metrics?.trendGranularity;
    return granularity === 'week' ? `Week of ${point.key}` :
      granularity === 'month' ? point.label : point.key;
  }

  showTrendPoint(point: TrendGraphPoint): void {
    this.activeTrendPoint = point;
  }

  hideTrendPoint(): void {
    this.activeTrendPoint = null;
  }

  showRequestTrendPoint(point: TrendGraphPoint): void {
    this.activeRequestTrendPoint = point;
  }

  hideRequestTrendPoint(): void {
    this.activeRequestTrendPoint = null;
  }

  trendTooltipX(point: TrendGraphPoint): number {
    return Math.max(0, Math.min(this.chartWidth - 190, point.x - 95));
  }

  trendTooltipY(point: TrendGraphPoint): number {
    return point.y >= 88 ? point.y - 78 : point.y + 12;
  }

  trackTrendPoint(_index: number, point: DashboardMetricPoint): string {
    return point.key;
  }

  trackDepartment(_index: number, item: { department: DashboardMetricPoint }): string {
    return item.department.key;
  }

  trackSummaryCard(_index: number, card: { label: string }): string {
    return card.label;
  }

  get statusSegments(): { color: string; dashArray: string; dashOffset: number }[] {
    const points = this.metrics?.complaintStatuses ?? [];
    return this.createDonutSegments(
      points.map((point, index) => ({
        label: point.label,
        scopeLabel: '',
        value: point.value,
        color: this.colorForIndex(index),
      })),
    );
  }

  private createDonutSegments(points: { value: number; color: string }[]): {
    color: string;
    dashArray: string;
    dashOffset: number;
  }[] {
    const circumference = 2 * Math.PI * 43;
    const total = points.reduce((sum, point) => sum + point.value, 0);
    let offset = 0;
    return points.filter(point => point.value > 0).map(point => {
      const length = circumference * point.value / Math.max(total, 1);
      const segment = {
        color: point.color,
        dashArray: `${length} ${circumference}`,
        dashOffset: -offset,
      };
      offset += length;
      return segment;
    });
  }

  get trendMaxValue(): number {
    return this.getTrendMaxValue(this.metrics?.complaintTrend ?? []);
  }

  get requestTrendMaxValue(): number {
    return this.getTrendMaxValue(this.metrics?.userRequestMetrics?.requestTrend ?? []);
  }

  get trendTicks(): number[] {
    return this.getTrendTicks(this.trendMaxValue);
  }

  get trendHitRadius(): number {
    const count = this.metrics?.complaintTrend.length ?? 0;
    if (count < 2) {
      return 12;
    }
    const plotWidth = this.chartWidth - this.chartPaddingLeft - this.chartPaddingRight;
    return Math.min(12, plotWidth / (2 * (count - 1)));
  }

  get requestTrendTicks(): number[] {
    return this.getTrendTicks(this.requestTrendMaxValue);
  }

  get trendGraphPoints(): TrendGraphPoint[] {
    return this.createTrendGraphPoints(this.metrics?.complaintTrend ?? [], this.trendMaxValue);
  }

  get requestTrendGraphPoints(): TrendGraphPoint[] {
    return this.createTrendGraphPoints(
      this.metrics?.userRequestMetrics?.requestTrend ?? [],
      this.requestTrendMaxValue,
    );
  }

  get trendLinePath(): string {
    return this.createTrendLinePath(this.trendGraphPoints);
  }

  get requestTrendLinePath(): string {
    return this.createTrendLinePath(this.requestTrendGraphPoints);
  }

  get trendAreaPath(): string {
    return this.createTrendAreaPath(this.trendGraphPoints);
  }

  get requestTrendAreaPath(): string {
    return this.createTrendAreaPath(this.requestTrendGraphPoints);
  }

  trendTickY(tick: number, maxValue = this.trendMaxValue): number {
    const plotHeight = this.chartHeight - this.chartPaddingTop - this.chartPaddingBottom;
    return this.chartPaddingTop + plotHeight * (1 - tick / Math.max(maxValue, 1));
  }

  private getTrendMaxValue(points: DashboardMetricPoint[]): number {
    const maximum = Math.max(0, ...points.map(point => point.value));
    return Math.max(1, maximum <= 4 ? 4 : Math.ceil(maximum / 4) * 4);
  }

  private getTrendTicks(maxValue: number): number[] {
    return [0, 1, 2, 3, 4].map(tick => Math.round(maxValue * tick / 4));
  }

  private createTrendGraphPoints(points: DashboardMetricPoint[], maxValue: number): TrendGraphPoint[] {
    // Reuse the complaint chart's coordinates and non-overlapping hit regions for admin request flow.
    const plotWidth = this.chartWidth - this.chartPaddingLeft - this.chartPaddingRight;
    const plotHeight = this.chartHeight - this.chartPaddingTop - this.chartPaddingBottom;
    const labelStep = Math.max(1, Math.ceil(points.length / 5));
    const step = points.length > 1 ? plotWidth / (points.length - 1) : plotWidth;

    return points.map((point, index) => {
      const x = this.chartPaddingLeft + (points.length <= 1 ? 0 : step * index);
      const hitX = index === 0 ? this.chartPaddingLeft - 8 : x - step / 2;
      const hitEnd = index === points.length - 1 ? this.chartWidth - this.chartPaddingRight + 8 : x + step / 2;
      return {
        ...point,
        x,
        y: this.chartPaddingTop + plotHeight * (1 - point.value / maxValue),
        hitX,
        hitWidth: hitEnd - hitX,
        showLabel: index === 0 || index === points.length - 1 || index % labelStep === 0,
      };
    });
  }

  private createTrendLinePath(points: TrendGraphPoint[]): string {
    return points
      .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
      .join(' ');
  }

  private createTrendAreaPath(points: TrendGraphPoint[]): string {
    if (points.length === 0) {
      return '';
    }
    const bottom = this.chartHeight - this.chartPaddingBottom;
    return `M ${points[0].x} ${bottom} ${points.map(point => `L ${point.x} ${point.y}`).join(' ')} L ${points[points.length - 1].x} ${bottom} Z`;
  }

  barWidth(point: DashboardMetricPoint, points: DashboardMetricPoint[]): number {
    const maximum = Math.max(1, ...points.map(item => item.value));
    return point.value === 0 ? 0 : Math.max(5, point.value / maximum * 100);
  }

  private loadDashboard(): void {
    this.requestSubscription?.unsubscribe();
    this.activeTrendPoint = null;
    this.activeRequestTrendPoint = null;
    this.loading = true;
    this.errorMessage = '';

    const fromDate = this.customRangeForm.controls.fromDate.value;
    const toDate = this.customRangeForm.controls.toDate.value;
    this.requestSubscription = this.dashboardService
      .getMetrics(
        this.selectedRange,
        fromDate ? this.formatDate(fromDate) : undefined,
        toDate ? this.formatDate(toDate) : undefined,
      )
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: metrics => {
          if (!Array.isArray(metrics.departmentSeverityBreakdown)) {
            this.metrics = null;
            this.errorMessage = 'Dashboard data is incomplete. Please update the server and try again.';
            return;
          }
          this.metrics = metrics;
        },
        error: () => {
          this.errorMessage = 'We could not load dashboard metrics. Please check your connection and try again.';
        },
      });
  }

  private shiftDate(date: Date, days: number): Date {
    const shifted = new Date(date);
    shifted.setDate(shifted.getDate() + days);
    return shifted;
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
